/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Else, If, Then, When } from "react-if";
import { Button, Spacer, Text, theme } from "@nordcloud/gnui";
import { ApprovalMode, ExecutionStatus } from "~/generated/graphql";
import { dateFormat } from "~/constants";
import { capitalize, isEmpty, isNil, isNotEmpty } from "~/tools";
import {
  APPROVAL_CHECKER_WAIT_INTERVAL_MINUTES,
  getEventEntityStartTime,
} from "~/views/events/components/ApprovalBadge/approvalMeta";
import { formatApprovalDuration } from "~/views/events/components/ApprovalBadge/formatApprovalDuration";
import { useEvent } from "~/views/events/EventsDetails/EventProvider";
import { RefetchOptions } from "~/views/events/helpers";
import { useApproveEvent } from "~/views/events/hooks";
import { EventEntity } from "~/views/events/types";

const APPROVAL_DEADLINE_EXPIRED = "Approval Timed out";

type Props = {
  eventEntity: EventEntity;
  isBatchEntity: boolean;
};

// eslint-disable-next-line complexity
export function ActionApprovalSection({ eventEntity, isBatchEntity }: Props) {
  const entityStatus = isBatchEntity
    ? eventEntity.status
    : eventEntity.globalState?.status;

  const isAsScheduledApprovalMode =
    eventEntity.approvalMode === ApprovalMode.AsScheduled;
  const approvalStartTime = getEventEntityStartTime(eventEntity);

  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (
      entityStatus !== ExecutionStatus.ApprovalRequired ||
      !isAsScheduledApprovalMode ||
      isNil(approvalStartTime)
    ) {
      return;
    }

    const timer = setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, [entityStatus, isAsScheduledApprovalMode, approvalStartTime]);

  const {
    event: { id: eventId },
  } = useEvent();

  const {
    approveEventAction,
    approveEventActionBatch,
    actionLoading,
    batchLoading,
  } = useApproveEvent({
    onSuccess: () => {},
    refetchQueries: RefetchOptions.EVENT,
  });

  const approvedMessage = getApprovedMessage(eventEntity, isBatchEntity);

  if (
    entityStatus !== ExecutionStatus.ApprovalRequired &&
    entityStatus !== ExecutionStatus.ApprovalTimeout &&
    isEmpty(approvedMessage)
  ) {
    return null;
  }

  const handleApprove = () => {
    if (!eventId) {
      return;
    }
    if (isBatchEntity) {
      void approveEventActionBatch(eventId, eventEntity.id);
    } else {
      void approveEventAction(eventId, eventEntity.id);
    }
  };

  const approvalExpireAt =
    isAsScheduledApprovalMode && approvalStartTime && eventEntity.windowDuration
      ? dayjs(approvalStartTime).add(
          eventEntity.windowDuration - APPROVAL_CHECKER_WAIT_INTERVAL_MINUTES,
          "minute"
        )
      : null;

  const leftTime = approvalExpireAt
    ? approvalExpireAt.diff(dayjs(now), "second")
    : 0;

  const entity = isBatchEntity ? "batch" : "action";
  const buttonLabel = `Approve ${capitalize(entity)}`;

  return (
    <>
      <Spacer height={theme.spacing.spacing04} />
      <div
        css={{
          borderTop: `1px solid ${theme.color.border.border01}`,
          paddingTop: theme.spacing.spacing04,
        }}
      >
        <If condition={isNotEmpty(approvedMessage)}>
          <Then>
            <Text color="success">{approvedMessage}</Text>
          </Then>
          <Else>
            <When condition={entityStatus === ExecutionStatus.ApprovalTimeout}>
              <Text color="danger">{APPROVAL_DEADLINE_EXPIRED}</Text>
              <Button
                disabled
                icon="checkmark"
                mr={theme.spacing.spacing02}
                status="accent"
              >
                {buttonLabel}
              </Button>
            </When>

            <When condition={entityStatus === ExecutionStatus.ApprovalRequired}>
              <If condition={leftTime > 0}>
                <Then>
                  <Text
                    color={theme.color.support.purple}
                    css={{ marginBottom: 0 }}
                  >
                    {formatApprovalDuration(leftTime)} to approve
                  </Text>
                  <Text size="sm">
                    Approval must occur by{" "}
                    <b>
                      {approvalExpireAt?.format(
                        dateFormat.dayMonthYearHourMinute
                      )}
                    </b>{" "}
                    (local)
                  </Text>
                </Then>
                <Else>
                  <Text color={theme.color.support.purple}>
                    {`This ${entity} is paused and requires approval`}
                  </Text>
                </Else>
              </If>
              <Button
                disabled={actionLoading || batchLoading}
                icon="checkmark"
                mr={theme.spacing.spacing02}
                status="accent"
                onClick={handleApprove}
              >
                {buttonLabel}
              </Button>
            </When>
          </Else>
        </If>
      </div>
    </>
  );
}

const getApprovedMessage = (entity: EventEntity, isBatchEntity: boolean) => {
  const { globalState } = entity;
  const approvedBy = getUserNameFromEmail(
    isBatchEntity ? entity.approvedBy ?? "" : globalState?.approvedBy ?? ""
  );
  const approvedAt = isBatchEntity
    ? entity.approvedAt ?? ""
    : globalState?.approvedAt ?? "";

  if (isEmpty(approvedBy) && isEmpty(approvedAt)) {
    return "";
  }

  if (isNotEmpty(approvedAt)) {
    const formatedApprovedAt = dayjs(approvedAt).format(
      dateFormat.dayMonthYearHourMinute
    );

    if (isNotEmpty(approvedBy)) {
      return `Approved by ${approvedBy} on ${formatedApprovedAt} (local)`;
    }

    return `Approved on ${formatedApprovedAt} (local)`;
  }

  return `Approved by ${approvedBy}`;
};

const getUserNameFromEmail = (email: string) => {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return email;
  }

  const namePart = email.split("@")[0];

  return namePart
    .replace(/\./g, " ")
    .split(" ")
    .filter(isNotEmpty)
    .map((word) => capitalize(word))
    .join(" ");
};
